import React from 'react';
import PropTypes from 'prop-types';
import { Grid, Stack, ShipsIcon, StorageIcon, TrendingUpIcon } from '@esgian/esgianui';
import { VesselsTable } from '@components/Tables';
import { useSegment } from '@hooks';
import KpiBox from '@components/Display/KpiBox';
import { formatNumberReadable, parseNumberToSignificant } from '@helpers';
import { useQuery } from '@tanstack/react-query';
import { getVesselsDetails } from '@api';
import { useSelector } from 'react-redux';
import { getCommercialCategoryType, getOperatorType } from '@store/features';
import { toast } from 'react-toastify';

function VesselsOverviewSection() {
  const { isRoRo, segment } = useSegment();
  const commercialCategoryType = useSelector(getCommercialCategoryType);
  const operatorType = useSelector(getOperatorType);

  const vesselsDetailsQuery = useQuery({
    queryKey: [
      'vesselsDetails',
      {
        segmentTypeId: segment.id,
        operatorTypeId: operatorType,
        commercialCategoryId: commercialCategoryType
      }
    ],
    enabled: !!segment.id,
    placeholderData: null,
    queryFn: ({ signal, queryKey }) => {
      return getVesselsDetails(signal, queryKey[1])
        .then(({ capacityStats, ageStats, vessels }) => {
          const { averageCEU, averageDeadweightTonnage, totalCEU, totalDeadweightTonnage } =
            capacityStats || {};
          return {
            kpiData: {
              avgAge: parseNumberToSignificant(ageStats.averageAge ?? 0),
              numVessels: vessels.length,
              avgCapacity: parseNumberToSignificant(isRoRo ? averageCEU : averageDeadweightTonnage),
              totCapacity: parseNumberToSignificant(isRoRo ? totalCEU : totalDeadweightTonnage)
            },
            tableData: vessels?.map((vessel) => {
              return {
                formerVesselName:
                  vessel.vesselNameHistory?.map(({ name }) => name).join(', ') || null,
                loadCapacity: vessel.capacity,
                ...vessel
              };
            })
          };
        })
        .catch((error) => {
          if (error.name !== 'AbortError') {
            toast.error(`Unable to fetch vessel details`, {
              position: 'top-right'
            });
          }
          return null;
        });
    }
  });

  let hideColumns = ['numCranes', 'maxLiftingCapacity', 'grossTonnage', 'deadweight'];
  if (!isRoRo) {
    hideColumns = ['callSign', 'ownerLongName', 'loadCapacity', 'grossTonnage', 'class'];
  }

  const { tableData = [], kpiData: { numVessels, avgAge, avgCapacity, totCapacity } = {} } =
    vesselsDetailsQuery.data ?? {};

  return (
    <Grid container spacing={2}>
      <Grid item xs={12}>
        <Stack
          direction="row"
          spacing={1} // Applies spacing between items on the same row
          useFlexGap
          sx={{ flexWrap: 'wrap' }}>
          <KpiBox
            loading={vesselsDetailsQuery.isFetching}
            icon={<ShipsIcon />}
            value={vesselsDetailsQuery.data ? formatNumberReadable(numVessels) : '-'}
            title={'Fleet Size'}
            subtitle={'Vessels'}
          />
          <KpiBox
            loading={vesselsDetailsQuery.isFetching}
            icon={<TrendingUpIcon />}
            value={vesselsDetailsQuery.data ? formatNumberReadable(avgAge) : '-'}
            title={'Average Age'}
            subtitle={'Years'}
          />
          <KpiBox
            loading={vesselsDetailsQuery.isFetching}
            icon={<StorageIcon />}
            value={vesselsDetailsQuery.data ? formatNumberReadable(avgCapacity) : '-'}
            title={'Average Capacity'}
            subtitle={isRoRo ? 'CEU' : 'DWT'}
          />
          <KpiBox
            loading={vesselsDetailsQuery.isFetching}
            icon={<StorageIcon />}
            value={vesselsDetailsQuery.data ? formatNumberReadable(totCapacity) : '-'}
            title={'Total Capacity'}
            subtitle={isRoRo ? 'CEU' : 'DWT'}
          />
        </Stack>
      </Grid>
      <Grid item xs={12}>
        <VesselsTable
          title={'Vessels'}
          hideColumns={hideColumns}
          vessels={tableData}
          fileName={'vessels-overview'}
          loading={vesselsDetailsQuery.isFetching}
        />
      </Grid>
    </Grid>
  );
}

VesselsOverviewSection.propTypes = {
  loading: PropTypes.bool,
  vesselsData: PropTypes.object
};

VesselsOverviewSection.defaultProps = {
  loading: false,
  vesselsData: null
};

export default VesselsOverviewSection;
